import PaginationControls from '@/components/shared/PaginationControls';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { DASHBOARD_PRESETS } from '@/components/filters/date-range-presets';
import { SortableColumnHeader } from '@/components/table/SortableColumnHeader';
import TableSkeleton from '@/components/table/TableSkeleton';
import { TableScrollArea } from '@/components/table/TableScrollArea';
import { hasDashboardTableDateFilter } from '@/components/table/dashboard-table-utils';
import { useDashboardTableQueryState } from '@/components/table/useDashboardTableQueryState';
import Money from '@/components/text/money';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPanel } from '@/components/ui/filter-panel';
import StatCard from '@/components/stats/StatCard';
import StatCardSkeleton from '@/components/stats/StatCardSkeleton';
import { usePartnerSuccessFeesQuery } from '@/hooks/queries/use-partner-success-fees-query';
import { formatSuccessFeeRate } from '@/libs/utils/format-success-fee-rate';
import { Badge, Box, Button, Grid, Link, Table, Text, VStack } from '@chakra-ui/react';
import {
  PARTNER_SUCCESS_FEE_DATE_FILTER_TYPES,
  PARTNER_SUCCESS_FEE_SORT_BY,
  type DashboardTableQuery,
  type DashboardTableSortState,
  type PartnerSuccessFeeBillingQuery,
  type PartnerSuccessFeeBillingState,
  type PartnerSuccessFeeBillingSummary,
  type PartnerSuccessFeeDateFilterType,
  type PartnerSuccessFeeSortBy,
} from '@fluenceur/types';
import { Coins, OpenNewWindow } from 'iconoir-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsSection from '../SettingsSection';

export const PARTNER_SUCCESS_FEE_TABLE_NAMESPACE = 'partnerSuccessFees';
const DEFAULT_SORT: DashboardTableSortState<PartnerSuccessFeeSortBy> = {
  sortBy: 'partnerValidationAt',
  sortDirection: 'desc',
};
const SKELETON_COLUMNS = [
  { width: '180px' },
  { width: '150px' },
  { width: '130px' },
  { width: '130px' },
  { width: '110px' },
  { width: '140px', type: 'date' as const },
  { width: '170px', type: 'date' as const },
  { width: '150px' },
  { width: '130px' },
];

const statePalette: Record<
  PartnerSuccessFeeBillingState,
  'gray' | 'blue' | 'green' | 'orange' | 'yellow'
> = {
  awaiting_validation: 'yellow',
  awaiting_qualification: 'orange',
  to_invoice: 'orange',
  invoiced: 'blue',
  paid: 'green',
  corrected: 'gray',
};

const padCalendarPart = (value: number): string => String(value).padStart(2, '0');

export const formatLocalCalendarDate = (date: Date): string =>
  `${date.getFullYear()}-${padCalendarPart(date.getMonth() + 1)}-${padCalendarPart(date.getDate())}`;

export function initialPartnerSuccessFeePeriod(now = new Date()) {
  return {
    from: formatLocalCalendarDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: formatLocalCalendarDate(now),
  };
}

export function buildPartnerSuccessFeeQuery(
  tableQuery: DashboardTableQuery<PartnerSuccessFeeSortBy, PartnerSuccessFeeDateFilterType>,
  defaults: { from: string; to: string }
): PartnerSuccessFeeBillingQuery {
  const startDate = tableQuery.startDate ?? defaults.from;
  const endDate = tableQuery.endDate ?? defaults.to;

  return {
    ...tableQuery,
    dateFilterType: tableQuery.dateFilterType ?? 'partnerValidationAt',
    startDate: toLocalDateBoundary(startDate, 'start'),
    endDate: toLocalDateBoundary(endDate, 'end'),
  };
}

function toLocalDateBoundary(value: string, boundary: 'start' | 'end'): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const time = boundary === 'start' ? '00:00:00' : '23:59:59.999';
  return new Date(`${value}T${time}`).toISOString();
}

export function PartnerSuccessFeesSection() {
  const { t } = useTranslation();
  const locale = t('common.locale');
  const defaults = useMemo(initialPartnerSuccessFeePeriod, []);
  const tableState = useDashboardTableQueryState<
    PartnerSuccessFeeSortBy,
    PartnerSuccessFeeDateFilterType
  >({
    namespace: PARTNER_SUCCESS_FEE_TABLE_NAMESPACE,
    sortByOptions: PARTNER_SUCCESS_FEE_SORT_BY,
    defaultSort: DEFAULT_SORT,
    dateFilterTypeOptions: PARTNER_SUCCESS_FEE_DATE_FILTER_TYPES,
    defaultDateFilterType: 'partnerValidationAt',
  });
  const displayedDateFilter = {
    dateFilterType: tableState.dateFilter.dateFilterType ?? 'partnerValidationAt',
    startDate: tableState.dateFilter.startDate ?? defaults.from,
    endDate: tableState.dateFilter.endDate ?? defaults.to,
  };
  const hasValidPeriod =
    /^\d{4}-\d{2}-\d{2}$/.test(displayedDateFilter.startDate) &&
    /^\d{4}-\d{2}-\d{2}$/.test(displayedDateFilter.endDate) &&
    displayedDateFilter.startDate <= displayedDateFilter.endDate;
  const queryInput = useMemo(
    () => buildPartnerSuccessFeeQuery(tableState.query, defaults),
    [defaults, tableState.query]
  );
  const query = usePartnerSuccessFeesQuery(queryInput, hasValidPeriod);
  const summary = query.data;

  return (
    <SettingsSection
      title={t('settings.subscription.partnerSuccessFees.title')}
      description={t('settings.subscription.partnerSuccessFees.description')}
      layout="stacked"
      isLast
    >
      <VStack align="stretch" gap={5}>
        <FilterPanel>
          <DateRangeFilter
            paramPrefix={PARTNER_SUCCESS_FEE_TABLE_NAMESPACE}
            presets={DASHBOARD_PRESETS}
            showPresets
            defaultPreset="this_month"
          />
        </FilterPanel>

        {query.isLoading ? (
          <>
            <MetricSkeletons />
            <TableSkeleton rows={5} columns={SKELETON_COLUMNS} stickyColumns={0} />
          </>
        ) : query.isError ? (
          <Box borderWidth="1px" borderColor="red.muted" p={4} borderRadius="md">
            <VStack align="start" gap={3}>
              <Text color="red.fg">{t('settings.subscription.partnerSuccessFees.error')}</Text>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void query.refetch()}
              >
                {t('common.retry')}
              </Button>
            </VStack>
          </Box>
        ) : summary ? (
          <>
            <SummaryMetrics summary={summary} locale={locale} />
            <BillingTable
              summary={summary}
              locale={locale}
              sort={tableState.sort}
              onSortChange={tableState.setSort}
              hasActiveFilters={hasDashboardTableDateFilter(displayedDateFilter)}
            />
            {summary.pagination.total > 0 ? (
              <PaginationControls
                pagination={{
                  total: summary.pagination.total,
                  perPage: summary.pagination.perPage,
                  currentPage: summary.pagination.page,
                  lastPage: summary.pagination.lastPage,
                  from: (summary.pagination.page - 1) * summary.pagination.perPage + 1,
                  to: Math.min(
                    summary.pagination.page * summary.pagination.perPage,
                    summary.pagination.total
                  ),
                }}
                onPageChange={tableState.setPage}
                isLoading={query.isFetching}
              />
            ) : null}
          </>
        ) : null}
      </VStack>
    </SettingsSection>
  );
}

function SummaryMetrics({
  summary,
  locale,
}: {
  summary: PartnerSuccessFeeBillingSummary;
  locale: string;
}) {
  const { t } = useTranslation();
  return (
    <Grid templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }} gap={3}>
      <StatCard
        title={t('settings.subscription.partnerSuccessFees.rate')}
        description={t('settings.subscription.partnerSuccessFees.rate')}
        value={
          summary.currentRateBasisPoints === null
            ? '—'
            : formatSuccessFeeRate(summary.currentRateBasisPoints, locale)
        }
      />
      <StatCard
        title={t('settings.subscription.partnerSuccessFees.revenue')}
        description={t('settings.subscription.partnerSuccessFees.revenue')}
        value={formatCurrencyTotals(summary.totals, 'retainedRevenueMinorUnits', locale)}
      />
      <StatCard
        title={t('settings.subscription.partnerSuccessFees.feeTotal')}
        description={t('settings.subscription.partnerSuccessFees.feeTotal')}
        value={formatCurrencyTotals(summary.totals, 'feeTotalMinorUnits', locale)}
      />
    </Grid>
  );
}

function formatCurrencyTotals(
  totals: PartnerSuccessFeeBillingSummary['totals'],
  field: 'retainedRevenueMinorUnits' | 'feeTotalMinorUnits',
  locale: string
): string {
  if (totals.length === 0) return '—';
  return totals
    .map((total) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: total.currency,
      }).format(total[field] / 100)
    )
    .join(' · ');
}

function BillingTable({
  summary,
  locale,
  sort,
  onSortChange,
  hasActiveFilters,
}: {
  summary: PartnerSuccessFeeBillingSummary;
  locale: string;
  sort: DashboardTableSortState<PartnerSuccessFeeSortBy>;
  onSortChange: (sort: DashboardTableSortState<PartnerSuccessFeeSortBy>) => void;
  hasActiveFilters: boolean;
}) {
  const { t } = useTranslation();
  const sortableHeader = (column: PartnerSuccessFeeSortBy) => ({
    column,
    sort,
    defaultSort: DEFAULT_SORT,
    onSortChange,
  });

  // Nothing to show means no table at all, as on every other list: nine sortable
  // headers over no rows are furniture around an absence.
  if (summary.rows.length === 0) {
    const filtered = summary.hasAnyRows && hasActiveFilters;

    return (
      <EmptyState
        icon={<Coins />}
        title={t(
          filtered
            ? 'settings.subscription.partnerSuccessFees.emptyFilteredTitle'
            : 'settings.subscription.partnerSuccessFees.emptyNoDataTitle'
        )}
        description={t(
          filtered
            ? 'settings.subscription.partnerSuccessFees.emptyFilteredDescription'
            : 'settings.subscription.partnerSuccessFees.emptyNoDataDescription'
        )}
      />
    );
  }

  return (
    <TableScrollArea>
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.program')}
              {...sortableHeader('program')}
            />
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.sale')}
              {...sortableHeader('sale')}
            />
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.base')}
              textAlign="end"
              {...sortableHeader('revenueBasis')}
            />
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.fee')}
              textAlign="end"
              {...sortableHeader('feeAmount')}
            />
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.frozenRate')}
              textAlign="end"
              {...sortableHeader('feeRate')}
            />
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.sourceDate')}
              {...sortableHeader('sourceDate')}
            />
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.validation')}
              {...sortableHeader('partnerValidationAt')}
            />
            <SortableColumnHeader
              label={t('settings.subscription.partnerSuccessFees.billingState')}
              {...sortableHeader('billingState')}
            />
            <Table.ColumnHeader>
              {t('settings.subscription.partnerSuccessFees.document')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {summary.rows.map((row) => (
              <Table.Row key={row.id}>
                <Table.Cell>{row.programName ?? '—'}</Table.Cell>
                <Table.Cell>
                  <Text fontFamily="mono" fontSize="xs">
                    {row.saleReference}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="end">
                  <NullableMoney minorUnits={row.revenueBasisMinorUnits} currency={row.currency} />
                </Table.Cell>
                <Table.Cell textAlign="end">
                  <NullableMoney minorUnits={row.feeAmountMinorUnits} currency={row.currency} />
                </Table.Cell>
                <Table.Cell textAlign="end">
                  {row.feeRateBasisPoints === null
                    ? '—'
                    : formatSuccessFeeRate(row.feeRateBasisPoints, locale)}
                </Table.Cell>
                <Table.Cell>
                  <BillingDate value={row.sourceDate} locale={locale} />
                </Table.Cell>
                <Table.Cell>
                  <VStack align="start" gap={1}>
                    <BillingDate value={row.statusObservedAt} locale={locale} />
                    <Badge colorPalette={providerPalette(row.providerStatus)}>
                      {t(`settings.subscription.partnerSuccessFees.provider.${row.providerStatus}`)}
                    </Badge>
                  </VStack>
                </Table.Cell>
                <Table.Cell>
                  <Badge colorPalette={statePalette[row.billingState]}>
                    {t(`settings.subscription.partnerSuccessFees.state.${row.billingState}`)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  {row.invoiceDocumentUrl ? (
                    <Link
                      href={row.invoiceDocumentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                    >
                      {row.invoiceNumber ??
                        t('settings.subscription.partnerSuccessFees.viewDocument')}
                      <OpenNewWindow width={14} height={14} aria-hidden />
                    </Link>
                  ) : (
                    <Text color="fg.muted">—</Text>
                  )}
                </Table.Cell>
              </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </TableScrollArea>
  );
}

function NullableMoney({
  minorUnits,
  currency,
}: {
  minorUnits: number | null;
  currency: string | null;
}) {
  if (minorUnits === null || currency === null) {
    return (
      <Text fontSize="sm" color="fg.muted">
        —
      </Text>
    );
  }
  return <Money fontSize="sm" value={minorUnits / 100} currency={currency} />;
}

function BillingDate({ value, locale }: { value: string | null; locale: string }) {
  if (!value) {
    return (
      <Text fontSize="sm" color="fg.muted">
        —
      </Text>
    );
  }
  return (
    <Text fontSize="sm">
      {new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))}
    </Text>
  );
}

function providerPalette(
  status: PartnerSuccessFeeBillingSummary['rows'][number]['providerStatus']
): 'gray' | 'green' | 'red' | 'yellow' {
  if (status === 'validated') return 'green';
  if (status === 'rejected') return 'red';
  if (status === 'pending') return 'yellow';
  return 'gray';
}

function MetricSkeletons() {
  return (
    <Grid templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }} gap={3}>
      {[0, 1, 2].map((index) => (
        <StatCardSkeleton key={index} />
      ))}
    </Grid>
  );
}
